import { useEffect, useRef, useState } from 'react';

import { useMutateEvidenceReferralLink } from '@/hooks/mutation/useMutateEvidenceReferralLink';
import { useQueryUser } from '@/hooks/query/useQueryUser';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { LinkedinIcon, TwitterIcon } from '@heidi/design-system/icons';
import { Input } from '@heidi/design-system/input';
import {
  DialogDrawer,
  DialogDrawerContent,
} from '@heidi/design-system/mobile-responsive-dialog-drawer';
import { toast } from '@heidi/design-system/toast';
import {
  TypographyCaption,
  TypographyH4,
  TypographyH5,
  TypographyP2,
  TypographyP2Bold,
  typographyVariant,
} from '@heidi/design-system/typography';
import { isEvidenceReferralModalOpenAtom } from '@heidi/growth';
import { useTranslations } from '@heidi/runtime/i18n';
import { useAtom } from 'jotai';
import { Check, Copy, Gift, Link, Mail } from 'lucide-react';

interface EvidenceReferralModalProps {
  className?: string;
}

export const EvidenceReferralModal = ({ className }: EvidenceReferralModalProps) => {
  const { t } = useTranslations();
  const [isOpen, setIsOpen] = useAtom(isEvidenceReferralModalOpenAtom);
  const { data: user } = useQueryUser((data) => ({
    heidi_referral_code: data.heidi_referral_code as string | undefined,
  }));

  const [isCopied, setIsCopied] = useState(false);
  const copyTimeoutRef = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const {
    mutate: generateReferralLink,
    isPending,
    data: generatedLink,
  } = useMutateEvidenceReferralLink();

  const userReferralCode = user?.heidi_referral_code;

  // Auto-generate link when modal opens if user already has a referral code
  // This ensures we get the correct environment-aware URL from the backend
  useEffect(() => {
    if (isOpen && userReferralCode && !generatedLink) {
      generateReferralLink();
    }
  }, [isOpen, userReferralCode, generatedLink, generateReferralLink]);

  // Use the referral_url from the API response which includes environment-aware base URL and region
  const displayLink = generatedLink?.referral_url ?? null;
  const referralCode = generatedLink?.referral_code ?? userReferralCode;

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleGenerateLink = () => {
    generateReferralLink();
  };

  const handleCopyLink = async () => {
    if (!displayLink) return;

    try {
      await navigator.clipboard.writeText(displayLink);
      setIsCopied(true);
      toast({
        tone: 'success',
        title: t('evidenceReferral.linkCopied'),
        description: t('evidenceReferral.linkCopiedDescription'),
      });
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
      copyTimeoutRef.current = setTimeout(() => setIsCopied(false), 2000);
    } catch {
      toast({
        tone: 'destructive',
        title: t('evidenceReferral.copyFailed'),
        description: t('evidenceReferral.copyFailedDescription'),
      });
    }
  };

  const handleShareTwitter = () => {
    if (!displayLink) return;
    const text = encodeURIComponent(t('evidenceReferral.twitterShareText'));
    const url = encodeURIComponent(displayLink);
    window.open(
      `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleShareLinkedIn = () => {
    if (!displayLink) return;
    const url = encodeURIComponent(displayLink);
    window.open(
      `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      '_blank',
      'noopener,noreferrer'
    );
  };

  const handleShareEmail = () => {
    if (!displayLink) return;
    const subject = encodeURIComponent(t('evidenceReferral.emailSubject'));
    const body = encodeURIComponent(
      t('evidenceReferral.emailBody', { link: displayLink })
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  return (
    <DialogDrawer open={isOpen} onOpenChange={handleClose}>
      <DialogDrawerContent
        className={cn(
          'flex flex-col gap-4 overflow-hidden border-none p-0 sm:w-[480px] sm:max-w-lg',
          className
        )}
        data-testid="evidence-referral-modal"
      >
        {/* Header Section */}
        <div className="flex flex-col gap-3 bg-gradient-to-r from-skies-100 to-skies-50 p-6">
          <div className="flex items-center gap-2">
            <Gift className="size-5 text-text-active" />
            <TypographyCaption className="font-semibold uppercase tracking-wider text-text-active">
              {t('evidenceReferral.programBadge')}
            </TypographyCaption>
          </div>
          <TypographyH4>{t('evidenceReferral.shareTitle')}</TypographyH4>
          <TypographyP2 className="text-text-secondary">
            {t('evidenceReferral.shareDescription')}
          </TypographyP2>
        </div>

        {/* Content Section */}
        <div className="flex flex-col gap-6 p-6 pt-0">
          {isPending && !displayLink ? (
            /* Loading State */
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <TypographyP2 className="text-text-secondary">
                {t('evidenceReferral.loadingLink')}
              </TypographyP2>
            </div>
          ) : displayLink ? (
            <>
              {/* Referral Link Display */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <TypographyP2Bold>
                    {t('evidenceReferral.yourReferralLink')}
                  </TypographyP2Bold>
                  {referralCode && (
                    <TypographyCaption className="text-text-secondary">
                      {t('evidenceReferral.code')}{' '}
                      <span className="font-mono font-semibold">{referralCode}</span>
                    </TypographyCaption>
                  )}
                </div>
                <div className="flex gap-2">
                  <Input
                    value={displayLink}
                    readOnly
                    className="flex-1 font-mono text-sm"
                    accessoryStart={<Link size={16} className="text-text-secondary" />}
                  />
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => void handleCopyLink()}
                    className="shrink-0"
                  >
                    {isCopied ? <Check size={16} /> : <Copy size={16} />}
                    {isCopied ? t('evidenceReferral.copied') : t('evidenceReferral.copy')}
                  </Button>
                </div>
              </div>

              {/* Social Share Buttons */}
              <div className="flex flex-col gap-2">
                <TypographyP2Bold>{t('evidenceReferral.shareVia')}</TypographyP2Bold>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShareTwitter}
                    className="flex-1"
                  >
                    <TwitterIcon size={16} />
                    {t('evidenceReferral.twitterX')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShareLinkedIn}
                    className="flex-1"
                  >
                    <LinkedinIcon size={16} />
                    {t('evidenceReferral.linkedIn')}
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleShareEmail}
                    className="flex-1"
                  >
                    <Mail size={16} />
                    {t('evidenceReferral.email')}
                  </Button>
                </div>
              </div>

              {/* How it Works */}
              <div className="flex flex-col gap-2 rounded-lg bg-background-secondary p-4">
                <TypographyH5>{t('evidenceReferral.howItWorks')}</TypographyH5>
                <ul
                  className={cn(
                    'flex flex-col gap-1 text-text-secondary',
                    typographyVariant({ variant: 'p2' })
                  )}
                >
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-text-primary">1.</span>
                    {t('evidenceReferral.step1')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-text-primary">2.</span>
                    {t('evidenceReferral.step2')}
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="font-semibold text-text-primary">3.</span>
                    {t('evidenceReferral.step3')}
                  </li>
                </ul>
              </div>
            </>
          ) : (
            /* Generate Link State */
            <div className="flex flex-col items-center gap-4 py-4 text-center">
              <TypographyP2 className="text-text-secondary">
                {t('evidenceReferral.generateDescription')}
              </TypographyP2>
              <Button
                size="lg"
                onClick={handleGenerateLink}
                disabled={isPending}
                className="w-full sm:w-auto"
              >
                {isPending
                  ? t('evidenceReferral.generating')
                  : t('evidenceReferral.generateLink')}
              </Button>
            </div>
          )}
        </div>
      </DialogDrawerContent>
    </DialogDrawer>
  );
};

export default EvidenceReferralModal;
